import { useId, useMemo, useState, type ReactNode } from 'react';
import { LabelPill } from './LabelPill.js';

export type ThroughputChartPeriod = '24h' | '7d' | '30d';

export interface ThroughputBucket {
  sessions: number;
  kwh: number;
}

export interface ThroughputChartProps {
  /** Per-bucket data, oldest first. Length-flexible: 24 hourly buckets
   *  for '24h', 7 daily for '7d', 30 daily for '30d'. */
  data: ThroughputBucket[];
  /** Currently selected period. Controlled — caller owns the state. */
  period: ThroughputChartPeriod;
  /** Fires when the user taps a period chip. Caller updates `period`. */
  onPeriodChange: (next: ThroughputChartPeriod) => void;
  /** Optional list of allowed period chips. Defaults to all three. */
  allowedPeriods?: readonly ThroughputChartPeriod[];
  /** Custom footer content slot. When provided, replaces the default
   *  "N sessions · X kWh" summary footer. Wrapper styling stays
   *  consistent so caller-supplied content inherits the same chrome. */
  footer?: ReactNode;
}

// Record + ordered keys mirroring RevenueChart's pattern — exhaustive
// Record for non-null typing, array for render order.
const PERIOD_LOOKUP: Record<
  ThroughputChartPeriod,
  { label: string; pill: string; copy: string }
> = {
  '24h': { label: '24H', pill: 'LAST 24H', copy: 'last 24 hours' },
  '7d':  { label: '7D',  pill: 'LAST 7D',  copy: 'last 7 days' },
  '30d': { label: '30D', pill: 'LAST 30D', copy: 'last 30 days' },
};
const PERIOD_ORDER: ReadonlyArray<ThroughputChartPeriod> = ['24h', '7d', '30d'];

// Chart geometry constants. Mirrors DefaultAreaChart's viewBox + padding
// shape so the two charts feel like one product when stacked on a page.
// Extra right-side padding accommodates the second (kWh) axis ticks.
const VIEW_WIDTH = 600;
const VIEW_HEIGHT = 220;
const PADDING_LEFT = 40;
const PADDING_RIGHT = 40;
const PADDING_TOP = 14;
const PADDING_BOTTOM = 28;

/**
 * Throughput chart — bars for session count (left axis) + line for
 * kWh delivered (right axis). Pure SVG, no chart library; mirrors
 * the codebase's existing DefaultAreaChart pattern so admin and PWA
 * surfaces share one chart-rendering posture.
 *
 * Sibling to `RevenueChart` — same header shape (LabelPill +
 * period chip row + period pill), same period chip semantics,
 * same footer slot pattern. Differences:
 *   - Two metrics per bucket (sessions, kwh) vs one
 *   - Bar+line composition vs area fill
 *   - Dual Y axes (sessions on left, kwh on right)
 *
 * Colors:
 *   - Bars (sessions) use `var(--evi-accent)` — matches RevenueChart's
 *     primary visual. The user spec mentioned `var(--evi-power)`
 *     ("brand green") but the codebase's brand-green semantic is
 *     actually `--evi-accent`; `--evi-power` is the electrical-power
 *     yellow/gold token. Going with accent so the bars read as the
 *     primary metric on this surface, same as revenue is primary on
 *     RevenueChart.
 *   - Line (kWh) uses `var(--evi-info)` blue — strong contrast
 *     against accent (green/amber per theme) on every cell, and
 *     semantically distinct (info channel = "secondary signal").
 *
 * No legend — the dual-axis label rows + tooltip make the bar-vs-
 * line attribution obvious; a legend would crowd a small embed.
 * Right-axis kWh label visible (not hidden) so the second metric's
 * scale is readable.
 */
export function ThroughputChart({
  data,
  period,
  onPeriodChange,
  allowedPeriods,
  footer,
}: ThroughputChartProps) {
  const visiblePeriods: readonly ThroughputChartPeriod[] = allowedPeriods
    ? PERIOD_ORDER.filter((p) => allowedPeriods.includes(p))
    : PERIOD_ORDER;

  const active = PERIOD_LOOKUP[period];
  const id = useId().replace(/:/g, '');
  const allZero = data.length === 0 || data.every((b) => b.sessions === 0 && b.kwh === 0);

  // Cross-bucket totals for the default footer + tooltip kWh max.
  const totals = useMemo(() => {
    let sessions = 0;
    let kwh = 0;
    for (const b of data) {
      sessions += b.sessions;
      kwh += b.kwh;
    }
    return { sessions, kwh: Math.round(kwh * 10) / 10 };
  }, [data]);

  // Tooltip state — index of the hovered bucket, or null when no hover.
  const [hover, setHover] = useState<number | null>(null);

  // Geometry: bar slot widths, line points, axis ticks. Reused across
  // both render paths (chart + tooltip overlay) via useMemo.
  const geom = useMemo(() => buildGeometry(data, period), [data, period]);

  const hasCustomFooter = footer !== undefined;
  const renderDefaultFooter = !hasCustomFooter && !allZero;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <LabelPill tone="power" size="md">THROUGHPUT</LabelPill>
        <div
          role="tablist"
          aria-label="Throughput period"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'var(--evi-surface-2)',
            border: '1px solid var(--evi-border)',
            borderRadius: '999px',
            padding: '2px',
          }}
        >
          {visiblePeriods.map((value) => {
            const isActive = value === period;
            const opt = PERIOD_LOOKUP[value];
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onPeriodChange(value)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: isActive ? 'var(--evi-accent)' : 'transparent',
                  color: isActive ? '#001a08' : 'var(--evi-text-mute)',
                  font: 'inherit',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  padding: '5px 10px',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease, color 0.15s ease',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <LabelPill tone="mute">{active.pill}</LabelPill>
      </div>

      {allZero ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--evi-text-mute)',
            fontSize: '13px',
            textAlign: 'center',
            padding: '0 12px',
          }}
        >
          No sessions in the {active.copy}.
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <svg
            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
            width="100%"
            height={VIEW_HEIGHT}
            style={{ display: 'block', overflow: 'visible' }}
            role="img"
            aria-label={`Throughput chart — ${totals.sessions} sessions and ${totals.kwh} kWh ${active.copy}`}
            onMouseLeave={() => setHover(null)}
          >
            {/* Y-axis grid lines + left-axis tick labels (sessions) */}
            {geom.sessionTicks.map((tick, i) => (
              <g key={`s-tick-${i}`}>
                <line
                  x1={PADDING_LEFT}
                  x2={VIEW_WIDTH - PADDING_RIGHT}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="var(--evi-border)"
                  strokeWidth={1}
                  strokeDasharray={i === geom.sessionTicks.length - 1 ? undefined : '2 3'}
                  opacity={0.6}
                />
                <text
                  x={PADDING_LEFT - 6}
                  y={tick.y + 3}
                  textAnchor="end"
                  fontSize="10"
                  fill="var(--evi-text-mute)"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {tick.label}
                </text>
              </g>
            ))}
            {/* Right-axis tick labels (kWh) */}
            {geom.kwhTicks.map((tick, i) => (
              <text
                key={`k-tick-${i}`}
                x={VIEW_WIDTH - PADDING_RIGHT + 6}
                y={tick.y + 3}
                textAnchor="start"
                fontSize="10"
                fill="var(--evi-text-mute)"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tick.label}
              </text>
            ))}
            {/* Bars (sessions) — accent fill, rounded top corners.
                Hover detection: invisible wider rect overlay per slot
                so tooltip activates from anywhere in the slot's
                horizontal band, not just the visible bar. */}
            {geom.bars.map((bar, i) => (
              <g key={`bar-${i}`}>
                <rect
                  x={bar.slotX}
                  y={PADDING_TOP}
                  width={bar.slotWidth}
                  height={VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
                {bar.height > 0 && (
                  <rect
                    x={bar.x}
                    y={bar.y}
                    width={bar.width}
                    height={bar.height}
                    fill={`url(#${id}-bar-grad)`}
                    rx={2}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </g>
            ))}
            <defs>
              <linearGradient id={`${id}-bar-grad`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--evi-accent)" stopOpacity="0.95" />
                <stop offset="100%" stopColor="var(--evi-accent)" stopOpacity="0.55" />
              </linearGradient>
            </defs>
            {/* Line (kWh) — info-tone polyline overlaid on the bars.
                pointer-events:none so the bars' hover slots keep the
                tooltip wired without the line stealing events. */}
            <polyline
              points={geom.linePoints}
              fill="none"
              stroke="var(--evi-info)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{ pointerEvents: 'none' }}
            />
            {/* Line dots — small filled circles on each kWh point to
                anchor the line visually against bar peaks. */}
            {geom.linePointsArray.map((pt, i) => (
              <circle
                key={`dot-${i}`}
                cx={pt.x}
                cy={pt.y}
                r={hover === i ? 4 : 2.5}
                fill="var(--evi-info)"
                stroke="var(--evi-bg)"
                strokeWidth={1}
                style={{ pointerEvents: 'none' }}
              />
            ))}
            {/* X-axis labels — show "NOW" / "TODAY" at the rightmost
                slot and step labels at midpoints. Skip every-other
                label at 30d to avoid crowding. */}
            {geom.xLabels.map((lbl, i) => (
              <text
                key={`x-lbl-${i}`}
                x={lbl.x}
                y={VIEW_HEIGHT - PADDING_BOTTOM + 16}
                textAnchor="middle"
                fontSize="10"
                fill="var(--evi-text-mute)"
              >
                {lbl.text}
              </text>
            ))}
            {/* Hover vertical guide line */}
            {hover !== null && (
              <line
                x1={geom.bars[hover]!.slotX + geom.bars[hover]!.slotWidth / 2}
                x2={geom.bars[hover]!.slotX + geom.bars[hover]!.slotWidth / 2}
                y1={PADDING_TOP}
                y2={VIEW_HEIGHT - PADDING_BOTTOM}
                stroke="var(--evi-text-mute)"
                strokeWidth={1}
                strokeDasharray="3 3"
                opacity={0.5}
                style={{ pointerEvents: 'none' }}
              />
            )}
          </svg>
          {/* Tooltip — absolute-positioned HTML so it can use real
              CSS layout without SVG text-positioning math. Renders
              above the SVG; pointer-events:none so it doesn't
              re-trigger hover. */}
          {hover !== null && data[hover] && (
            <div
              style={{
                position: 'absolute',
                left: `${((geom.bars[hover]!.slotX + geom.bars[hover]!.slotWidth / 2) / VIEW_WIDTH) * 100}%`,
                top: 4,
                transform: 'translateX(-50%)',
                background: 'var(--evi-surface)',
                border: '1px solid var(--evi-border)',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--evi-text)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                fontVariantNumeric: 'tabular-nums',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <span style={{ color: 'var(--evi-accent)' }}>
                {data[hover].sessions} {data[hover].sessions === 1 ? 'session' : 'sessions'}
              </span>
              {' · '}
              <span style={{ color: 'var(--evi-info)' }}>
                {data[hover].kwh.toFixed(1)} kWh
              </span>
            </div>
          )}
        </div>
      )}

      {(hasCustomFooter || renderDefaultFooter) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginTop: '8px',
            paddingTop: '10px',
            borderTop: '1px solid var(--evi-border)',
          }}
        >
          {hasCustomFooter ? (
            footer
          ) : (
            <>
              <LabelPill tone="power">TOTAL</LabelPill>
              <span>
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: 'var(--evi-accent)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {totals.sessions}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: 'var(--evi-mute)',
                    marginLeft: '4px',
                    letterSpacing: '1px',
                  }}
                >
                  {totals.sessions === 1 ? 'SESSION' : 'SESSIONS'}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: 'var(--evi-mute)',
                    marginLeft: '6px',
                    letterSpacing: '1px',
                  }}
                >
                  ·
                </span>
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: 'var(--evi-info)',
                    fontVariantNumeric: 'tabular-nums',
                    marginLeft: '6px',
                  }}
                >
                  {totals.kwh.toFixed(1)}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: 'var(--evi-mute)',
                    marginLeft: '4px',
                    letterSpacing: '1px',
                  }}
                >
                  KWH
                </span>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Geometry — compute bar slots + line points + axis ticks + x-labels.
// Pure function so the chart's render can stay declarative. Mirrors
// DefaultAreaChart's buildGeometry shape so a future shared helper
// extraction has a clean target.
// ──────────────────────────────────────────────────────────────────────

interface Geometry {
  bars: Array<{ slotX: number; slotWidth: number; x: number; y: number; width: number; height: number }>;
  linePoints: string;
  linePointsArray: Array<{ x: number; y: number }>;
  sessionTicks: Array<{ y: number; label: string }>;
  kwhTicks: Array<{ y: number; label: string }>;
  xLabels: Array<{ x: number; text: string }>;
}

function buildGeometry(data: ThroughputBucket[], period: ThroughputChartPeriod): Geometry {
  const n = data.length;
  const plotLeft = PADDING_LEFT;
  const plotRight = VIEW_WIDTH - PADDING_RIGHT;
  const plotTop = PADDING_TOP;
  const plotBottom = VIEW_HEIGHT - PADDING_BOTTOM;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  if (n === 0) {
    return {
      bars: [],
      linePoints: '',
      linePointsArray: [],
      sessionTicks: [],
      kwhTicks: [],
      xLabels: [],
    };
  }

  const maxSessions = Math.max(1, ...data.map((b) => b.sessions));
  const maxKwh = Math.max(0.1, ...data.map((b) => b.kwh));

  // Round axis maxes up to a clean tick so labels read as round numbers.
  const sessionsMax = niceCeil(maxSessions);
  const kwhMax = niceCeil(maxKwh);

  const slotWidth = plotW / n;
  // Bar visible width = 70% of slot, centered. 70% feels right for
  // dense 30d windows + airy 7d windows.
  const barWidth = Math.max(2, slotWidth * 0.7);
  const barInset = (slotWidth - barWidth) / 2;

  const bars = data.map((b, i) => {
    const slotX = plotLeft + i * slotWidth;
    const height = (b.sessions / sessionsMax) * plotH;
    const x = slotX + barInset;
    const y = plotBottom - height;
    return { slotX, slotWidth, x, y, width: barWidth, height };
  });

  const linePointsArray = data.map((b, i) => {
    const x = plotLeft + i * slotWidth + slotWidth / 2;
    const y = plotBottom - (b.kwh / kwhMax) * plotH;
    return { x, y };
  });
  const linePoints = linePointsArray.map((pt) => `${pt.x},${pt.y}`).join(' ');

  // Y-axis ticks at 0, halfway, max. Three ticks balance readability
  // and visual airiness.
  const sessionTicks = [
    { y: plotBottom, label: '0' },
    { y: plotTop + plotH / 2, label: String(Math.round(sessionsMax / 2)) },
    { y: plotTop, label: String(sessionsMax) },
  ];
  const kwhTicks = [
    { y: plotBottom, label: '0' },
    { y: plotTop + plotH / 2, label: formatKwh(kwhMax / 2) },
    { y: plotTop, label: formatKwh(kwhMax) },
  ];

  // X-axis labels. 24h → every 6h tick; 7d → daily; 30d → every 5d
  // to avoid crowding. Final label is "NOW" / "TODAY" per period.
  const xLabels: Array<{ x: number; text: string }> = [];
  const isHourly = period === '24h';
  const labelEvery = period === '30d' ? 5 : period === '7d' ? 1 : 6;
  for (let i = 0; i < n; i++) {
    if ((n - 1 - i) % labelEvery !== 0) continue;
    const stepsAgo = n - 1 - i;
    const x = plotLeft + i * slotWidth + slotWidth / 2;
    let text: string;
    if (isHourly) {
      text = stepsAgo === 0 ? 'NOW' : `-${stepsAgo}h`;
    } else {
      text = stepsAgo === 0 ? 'TODAY' : `-${stepsAgo}d`;
    }
    xLabels.push({ x, text });
  }

  return { bars, linePoints, linePointsArray, sessionTicks, kwhTicks, xLabels };
}

/** Round up to a "nice" number for axis maxes — 1, 2, 5, 10, 20, 50, 100, ... */
function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const base = Math.pow(10, exp);
  const norm = n / base;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

function formatKwh(n: number): string {
  if (n >= 100) return String(Math.round(n));
  if (n >= 10) return n.toFixed(0);
  return n.toFixed(1);
}
